import { useEffect, useState } from "react";
import { Badge, Button, Input } from "../ui/controls.tsx";

type AuthConfig = { google: boolean; dev: boolean; devUsers: { email: string; name: string; role: string }[] };

const ERRORS: Record<string, (email: string) => string> = {
  not_allowed: (email) => `${email || "That account"} isn't set up in HHL Ops yet. Ask an admin to add you under Settings → Users.`,
  expired: () => "The sign-in took too long. Please try again.",
  cancelled: () => "Sign-in was cancelled.",
  google: () => "Google sign-in didn't work. Please try again.",
};

export function LoginPage() {
  const params = new URLSearchParams(location.search);
  const next = params.get("next") ?? "/";
  const error = params.get("error");
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [unreachable, setUnreachable] = useState(false);
  useEffect(() => {
    fetch("/auth/config")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setConfig)
      .catch(() => setUnreachable(true));
  }, []);

  return (
    <div className="flex h-full items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-xl border border-line bg-surface p-6 shadow-pop">
        <div className="flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-7" />
          <div className="text-[17px] font-semibold">HHL Ops</div>
        </div>
        <p className="mt-1 text-muted">Hand Held Legend operations</p>
        {error && <div className="mt-4 rounded-md bg-danger-soft px-3 py-2 text-danger">{(ERRORS[error] ?? ERRORS.google)(params.get("email") ?? "")}</div>}
        {unreachable && (
          <div className="mt-4 rounded-md bg-warn-soft px-3 py-2 text-warn">Can't reach the HHL Ops server right now. Try again in a moment.</div>
        )}
        <div className="mt-5 flex flex-col gap-2">
          {config?.google && (
            <a
              href={`/auth/google?next=${encodeURIComponent(next)}`}
              className="flex h-9 items-center justify-center gap-2 rounded-md border border-line bg-surface font-medium hover:bg-hover"
            >
              <GoogleMark />
              Sign in with Google
            </a>
          )}
          {config && !config.google && !config.dev && <div className="text-muted">Google sign-in isn't configured on this server.</div>}
          {config && <WorkstationCode />}
          {config?.dev && (
            <div className="mt-2">
              <div className="mb-1.5 flex items-center gap-2 text-[12px] text-muted">
                Dev sign-in <Badge tone="warn">local only</Badge>
              </div>
              {config.devUsers.map((u) => (
                <a
                  key={u.email}
                  href={`/auth/dev?email=${encodeURIComponent(u.email)}&next=${encodeURIComponent(next)}`}
                  className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-hover"
                >
                  <span>{u.name || u.email}</span>
                  <span className="text-[12px] text-faint">{u.role}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Workstations (migration 072): a shared PC (the shippers' station) signs in with a one-time code an admin gets in
 * Settings → Users → the workstation → Get a sign-in code, and stays signed in for a year.
 */
function WorkstationCode() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open)
    return (
      <button type="button" onClick={() => setOpen(true)} className="mt-2 text-[12px] text-muted hover:text-text hover:underline">
        Set up this computer as a workstation
      </button>
    );
  const submit = async () => {
    setBusy(true);
    setError("");
    const res = await fetch("/auth/workstation", {
      method: "POST",
      headers: { "content-type": "application/json", "x-requested-with": "hhl-ops" },
      body: JSON.stringify({ code }),
    }).catch(() => null);
    if (res?.ok) return location.assign("/");
    const body = res ? ((await res.json().catch(() => ({}))) as { error?: string }) : {};
    setError(body.error ?? "Can't reach the HHL Ops server right now. Try again in a moment.");
    setBusy(false);
  };
  return (
    <form
      className="mt-3 flex flex-col gap-2 border-t border-line pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="text-[12px] text-muted">
        An admin gets the code in Settings → Users → the workstation → Get a sign-in code. It works once, for 15 minutes; this computer then stays signed in.
      </div>
      <Input
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="ABCD-2345"
        aria-label="Workstation code"
        className="text-center font-mono text-[16px] tracking-widest"
      />
      {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12px] text-danger">{error}</div>}
      <Button type="submit" variant="primary" disabled={busy || code.replace(/[^A-Za-z0-9]/g, "").length < 8}>
        {busy ? "Signing in…" : "Sign in this computer"}
      </Button>
    </form>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="size-4" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}
